/**
 * School-level platform admin actions: the per-school actions menu, the
 * invite / plan / seats / seat-request modal triggers, subscription toggling,
 * Stripe sync and the school usage reset.
 */
import { useState } from 'react';
import { apiRequest } from '../../../../services/apiClient';
import { confirmDialog, showDialog } from '../../../../utils/dialogs';
import { useToast } from '../../../../context/ToastContext';
import type { Translate } from '../platformAdminFormat';
import type { School, SchoolBillingSeed } from '../platformAdminTypes';

export interface SchoolModalState {
  visible: boolean;
  schoolId?: number;
}

export interface SchoolSeededModalState extends SchoolModalState {
  seed?: SchoolBillingSeed;
}

export interface SchoolSeatModalState extends SchoolModalState {
  current?: number;
}

export function useSchoolActions(t: Translate, loadPlatformData: () => Promise<void>) {
  const { showToast } = useToast();
  const [resettingSchoolUsageId, setResettingSchoolUsageId] = useState<number | null>(null);

  // Modal states
  const [showAdminModal, setShowAdminModal] = useState<SchoolModalState>({ visible: false });
  const [showBillModal, setShowBillModal] = useState<SchoolSeededModalState>({ visible: false });
  const [showPlanModal, setShowPlanModal] = useState<SchoolSeededModalState>({ visible: false });
  const [showSeatModal, setShowSeatModal] = useState<SchoolSeatModalState>({ visible: false });
  const [showPlanChangeRequests, setShowPlanChangeRequests] = useState<SchoolModalState>({ visible: false });
  const [schoolSettingsMenu, setSchoolSettingsMenu] = useState<{ visible: boolean; school?: School }>({ visible: false });

  const closeSchoolSettingsMenu = () => setSchoolSettingsMenu({ visible: false });

  const handleOpenSchoolSettings = (school: School) => {
    setSchoolSettingsMenu({ visible: true, school });
  };

  const executeSchoolAction = (action: () => void | Promise<void>) => {
    closeSchoolSettingsMenu();
    setTimeout(() => {
      action();
    }, 0);
  };

  const handleResetSchoolUsage = async (school: School) => {
    const confirmationMessage = t('platformAdmin.resetSchoolUsageConfirm', {
      name: school.name,
    });

    const confirmed = await confirmDialog(
      t('platformAdmin.resetSchoolUsage'),
      confirmationMessage,
      t('common.confirm'),
      t('common.cancel'),
    );

    if (!confirmed) return;

    try {
      closeSchoolSettingsMenu();
      setResettingSchoolUsageId(school.id);
      const result = await apiRequest<{
        users_reset: number;
        subscription_credits_remaining: number;
      }>({
        endpoint: `/schools/platform/${school.id}/reset-usage`,
        method: 'POST',
      });

      const successMessage = t('platformAdmin.resetSchoolUsageSuccess', {
        users: result.users_reset,
        credits: result.subscription_credits_remaining,
      });
      showToast(successMessage, 'success');
      await loadPlatformData();
    } catch (error) {
      console.error('Failed to reset school usage', error);
      const message = t('platformAdmin.resetSchoolUsageError');
      showDialog(message);
    } finally {
      setResettingSchoolUsageId(null);
    }
  };

  const toggleSubscription = async (school: School) => {
    const nextStatus = school.subscription_status === 'active' ? 'canceled' : 'active';
    const deactivating = nextStatus === 'canceled';
    // ADM-3: a local override that never touches Stripe, so it needs an explicit confirm.
    const confirmed = await confirmDialog(
      t(deactivating ? 'platformAdmin.deactivateSchoolTitle' : 'platformAdmin.activateSchoolTitle'),
      t(deactivating ? 'platformAdmin.deactivateSchoolConfirm' : 'platformAdmin.activateSchoolConfirm', {
        school: school.name,
        students: school.current_students ?? 0,
        mentors: school.current_mentors ?? 0,
      }),
      t(deactivating ? 'platformAdmin.deactivateSchoolAction' : 'platformAdmin.activateSchoolAction'),
      t('common.cancel'),
    );
    if (!confirmed) return;
    try {
      await apiRequest<School>({
        endpoint: `/schools/${school.id}/subscription-status`,
        method: 'POST',
        data: { status: nextStatus },
      });
      await loadPlatformData();
      const message = t('platformAdmin.subscriptionStatusUpdated', { status: nextStatus });
      showToast(message, 'success');
    } catch (error: any) {
      const message = t('platformAdmin.subscriptionStatusError');
      showDialog(message);
      console.error('Failed to update subscription status', error);
    }
  };

  const syncStripe = async (school: School) => {
    try {
      const result = await apiRequest<{ subscription_status?: string }>({
        endpoint: `/billing/schools/${school.id}/sync-status`,
        method: 'POST',
      });
      await loadPlatformData();
      const status = result?.subscription_status || t('platformAdmin.unknownStatus');
      const message = t('platformAdmin.syncStripeSuccess', { status });
      showToast(message, 'success');
    } catch (error: any) {
      const message = t('platformAdmin.syncStripeError');
      showDialog(message);
      console.error('Failed to sync Stripe status', error);
    }
  };

  return {
    resettingSchoolUsageId,
    showAdminModal,
    setShowAdminModal,
    showBillModal,
    setShowBillModal,
    showPlanModal,
    setShowPlanModal,
    showSeatModal,
    setShowSeatModal,
    showPlanChangeRequests,
    setShowPlanChangeRequests,
    schoolSettingsMenu,
    closeSchoolSettingsMenu,
    handleOpenSchoolSettings,
    executeSchoolAction,
    handleResetSchoolUsage,
    toggleSubscription,
    syncStripe,
  };
}

export type SchoolActions = ReturnType<typeof useSchoolActions>;
